import { useCallback, useEffect, useRef, useState } from "react";

export type CarrierFetchState<T> =
  | { status: "loading" }
  | { status: "loaded"; data: T }
  | { status: "error" };

/**
 * Fetch on mount, with a refresh.
 *
 * Every view needs the same three states and the same cancel-on-unmount guard,
 * so they lived as six near-identical copies. The copies also swallowed the
 * failure and told the carrier to "try again" without giving them anything to
 * click — and a carrier has no other route to this data, so their only option
 * was reloading the page.
 *
 * `key` re-runs the fetch when it changes (a shipment id, a selected period).
 * The fetcher is read through a ref so callers don't have to memoise it.
 *
 * A refresh leaves the current data on screen and only raises `refreshing` —
 * the shipment page refreshes itself after a carrier responds to an issue, and
 * blanking the whole page for that would read as the page having broken.
 * Switching keys does clear, because the old data is now the wrong shipment.
 */
export function useCarrierFetch<T>(
  fetcher: () => Promise<T>,
  key: string = "",
): { state: CarrierFetchState<T>; refreshing: boolean; refresh: () => void } {
  const [state, setState] = useState<CarrierFetchState<T>>({
    status: "loading",
  });
  const [refreshing, setRefreshing] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const keyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    if (keyRef.current !== key) {
      keyRef.current = key;
      setState({ status: "loading" });
    }
    setRefreshing(true);
    fetcherRef
      .current()
      .then((data) => {
        if (!cancelled) setState({ status: "loaded", data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, attempt]);

  const refresh = useCallback(() => setAttempt((count) => count + 1), []);

  return { state, refreshing, refresh };
}
